import React, { useCallback, useMemo, useState } from "react";
import { Button, Icon, Textarea } from "@shared/components";
import { TIMEZONE } from "@shared/constants";
import { AlertComparizonType, AlertNotification, AlertValueType, PatientSettings } from "@shared/models";
import { useGetDifferenceAlertTextShort } from "@containers/Alert/hooks";
import { convertDateToTimezone, getFormattedDateTime } from "@shared/utils";
import { IconType } from "@shared/components/Icon/Icon";

import "./index.scss";

interface AlertReadConfirmModalProps {
  onClose: () => void;
  onSuccess: (comment: string | null) => void;
  alertNotification: AlertNotification;
  patientSettings: PatientSettings;
}

const SensorDataSessionMarkerModal = (props: AlertReadConfirmModalProps) => {
  const { onClose, onSuccess, alertNotification, patientSettings } = props;
  const [comment, setComment] = useState<string | null>(null);
  const differenceText = useGetDifferenceAlertTextShort(alertNotification, false);

  const isActiveSubmitButton = useMemo(() => {
    const MIN_CHARS = 5;
    return comment && comment.length >= MIN_CHARS;
  }, [comment]);

  const notificationValue = useMemo(() => {
    return Number(alertNotification.value).toFixed(0);
  }, [alertNotification.value]);

  const notificationSignalType = useMemo(() => {
    let type = String(alertNotification.signal_type).toUpperCase();

    if (alertNotification.value_type === AlertValueType.INSUFFICIENT_DATA) {
      type += ` - Insufficient Data`;
    }

    return type;
  }, [alertNotification.signal_type, alertNotification.value_type]);

  const percentageColor = useMemo(() => {
    return alertNotification.comparison_type === AlertComparizonType.ABOVE ? "green" : "red";
  }, [alertNotification.comparison_type]);

  const percentageIcon = useMemo(() => {
    return alertNotification.comparison_type === AlertComparizonType.ABOVE ? "percentage-up" : "percentage-down";
  }, [alertNotification.comparison_type]);

  const patientTimeZone = useMemo(() => {
    if (!patientSettings) return undefined;

    return patientSettings?.timezone || TIMEZONE.UTC;
  }, [patientSettings]);

  const handleCommentChange = useCallback((event: React.FormEvent<HTMLTextAreaElement>) => {
    setComment((event.target as any).value);
  }, []);

  const isAbsolute = useMemo(() => {
    return alertNotification.value_type === AlertValueType.ABSOLUTE;
  }, [alertNotification.value_type]);

  const iconName = useMemo((): IconType => {
    if (alertNotification.alert_type)
      return `alert-${alertNotification.alert_type}-${alertNotification.comparison_type}`;

    return "alert-stable-parameter";
  }, [alertNotification]);

  return (
    <div className="alert-read-confirm modal-content-wrapper">
      <div className="modal-heading">Mark As Read</div>
      <div>
        <div className="modal-content">
          <div className="alert-read-confirm-values">
            <div className="alert-notification-row-value-wrapper">
              <div className="alert-notification-row-value-icon">
                <Icon type={iconName} />
              </div>
              <div className="alert-notification-row-value">
                <div className="alert-notification-row-value-top">
                  {alertNotification.value_type !== AlertValueType.INSUFFICIENT_DATA ? (
                    <span className="alert-notification-value">{notificationValue}</span>
                  ) : null}
                  <span className="alert-notification-signal-type">{notificationSignalType}</span>
                  {differenceText ? (
                    <Icon className="alert-notification-percentage-icon" type={percentageIcon} />
                  ) : null}
                  {differenceText ? (
                    <span className={`alert-notification-percentage-value ${percentageColor}`}>
                      {Number(differenceText).toFixed(0)}
                      {isAbsolute ? "" : "%"}
                    </span>
                  ) : null}
                </div>
                <div className="alert-notification-row-value-bottom">
                  {getFormattedDateTime(
                    patientTimeZone
                      ? (convertDateToTimezone(alertNotification.created_at, patientTimeZone) as Date)
                      : alertNotification.created_at,
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="form-rows">
            <div className="title-column">
              <Textarea
                type="text"
                labelTitle={""}
                name="comment"
                onChange={handleCommentChange}
                maxLength={500}
                placeholder="Please add the actions you did before closing this notification"
                cols={1}
              />
            </div>
          </div>
        </div>
        <div className="modal-actions">
          <Button appearance="subtle" className="subtle auto-width" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!isActiveSubmitButton}
            className="blue auto-width"
            onClick={onSuccess.bind(null, comment)}
          >
            Mark As Read
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SensorDataSessionMarkerModal;
